import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { AccessorySpec, AccessoryCategory, BatterySize, VideoInputType, RequiredItem, CableSuggestion } from '@/data/accessories';
import type { TargetFilter, SourceFilter } from './useCompatibilityRules';
import type { Camera } from '@/data/cameras';

interface DbAccessory {
  id: string;
  name: string;
  brand: string;
  category: AccessoryCategory;
  weight: number;
  dimensions: { width: number; height: number; depth: number } | null;
  mount_type: string[] | null;
  power_input: string[] | null;
  power_output: string[] | null;
  video_inputs: VideoInputType[] | null;
  video_outputs: string[] | null;
  battery_size: BatterySize | null;
  camera_compatibility: string[];
  accessory_compatibility: string[] | null;
  included_items: string[];
  required_items: RequiredItem[];
  optional_items: string[] | null;
  vent_obstruction_risk: boolean;
  conflict_points: string[] | null;
  price: number;
  image: string | null;
  suggested_cables: CableSuggestion[] | null;
  is_active: boolean;
  power_draw: number | null;
  capacity_wh: number | null;
  payload_capacity_kg: number | null;
  bowl_size: string[] | null;
}

interface CompatibilityRule {
  id: string;
  rule_type: string;
  source_type: string;
  source_id: string;
  target_type: string;
  target_id: string;
  relationship: 'compatible' | 'incompatible' | 'requires' | 'conflicts';
  is_active: boolean;
  // Category-based rule fields
  target_category: string | null;
  target_filter: TargetFilter | null;
  is_category_rule: boolean;
  notes: string | null;
  // Camera attribute-based rule fields
  source_filter: SourceFilter | null;
  is_global_rule: boolean;
}

// Transform database accessory to AccessorySpec format
const transformAccessory = (db: DbAccessory): AccessorySpec => ({
  id: db.id,
  name: db.name,
  brand: db.brand,
  category: db.category,
  weight: db.weight,
  dimensions: db.dimensions || undefined,
  mountType: db.mount_type || undefined,
  powerInput: db.power_input || undefined,
  powerOutput: db.power_output || undefined,
  videoInputs: db.video_inputs || undefined,
  videoOutputs: db.video_outputs || undefined,
  batterySize: db.battery_size || undefined,
  cameraCompatibility: db.camera_compatibility || [],
  accessoryCompatibility: db.accessory_compatibility || undefined,
  includedItems: db.included_items || [],
  requiredItems: (db.required_items || []) as RequiredItem[],
  optionalItems: db.optional_items || [],
  ventObstructionRisk: db.vent_obstruction_risk,
  conflictPoints: db.conflict_points || undefined,
  price: db.price,
  image: db.image || undefined,
  suggestedCables: db.suggested_cables || undefined,
  powerDraw: db.power_draw || undefined,
  capacityWh: db.capacity_wh || undefined,
  payloadCapacity: db.payload_capacity_kg || undefined,
  bowlSize: db.bowl_size || undefined,
});

// Check if an accessory matches a target filter
const matchesFilter = (accessory: AccessorySpec, filter: TargetFilter | null): boolean => {
  if (!filter) return true;
  
  // Check name_contains filter
  if (filter.name_contains) {
    const nameLower = accessory.name.toLowerCase();
    const filterLower = filter.name_contains.toLowerCase();
    if (!nameLower.includes(filterLower)) return false;
  }
  
  // Check brand filter
  if (filter.brand) {
    if (accessory.brand.toLowerCase() !== filter.brand.toLowerCase()) return false;
  }
  
  // Check any_of filter (accessory name/brand/mountType must contain at least one of the terms - OR logic)
  if (filter.any_of && filter.any_of.length > 0) {
    const mountTypeText = accessory.mountType?.join(' ') || '';
    const searchText = `${accessory.name} ${accessory.brand} ${mountTypeText}`.toLowerCase();
    const matches = filter.any_of.some(term => searchText.includes(term.toLowerCase()));
    if (!matches) return false;
  }
  
  // Check all_of filter (accessory name/brand/mountType must contain ALL of the terms - AND logic)
  if (filter.all_of && filter.all_of.length > 0) {
    const mountTypeText = accessory.mountType?.join(' ') || '';
    const searchText = `${accessory.name} ${accessory.brand} ${mountTypeText}`.toLowerCase();
    const matches = filter.all_of.every(term => searchText.includes(term.toLowerCase()));
    if (!matches) return false;
  }
  
  // Check none_of filter (accessory name/brand/mountType must NOT contain any of these terms - EXCLUDE logic)
  if (filter.none_of && filter.none_of.length > 0) {
    const mountTypeText = accessory.mountType?.join(' ') || '';
    const searchText = `${accessory.name} ${accessory.brand} ${mountTypeText}`.toLowerCase();
    const hasExcluded = filter.none_of.some(term => searchText.includes(term.toLowerCase()));
    if (hasExcluded) return false;
  }
  
  return true;
};

// Check if a camera matches a source filter (for global rules)
const matchesCameraFilter = (camera: Camera, filter: SourceFilter | null): boolean => {
  if (!filter) return false;
  
  // Check ports_contains filter
  if (filter.ports_contains) {
    const hasPort = camera.ports.some(port => 
      port.toLowerCase().includes(filter.ports_contains!.toLowerCase())
    );
    if (!hasPort) return false;
  }
  
  // Check ports_any_of filter
  if (filter.ports_any_of && filter.ports_any_of.length > 0) {
    const hasAnyPort = filter.ports_any_of.some(filterPort =>
      camera.ports.some(port => port.toLowerCase().includes(filterPort.toLowerCase()))
    );
    if (!hasAnyPort) return false;
  }
  
  // Check video_outputs_contains filter
  if (filter.video_outputs_contains) {
    const hasOutput = camera.videoOutputs.some(output =>
      output.toLowerCase().includes(filter.video_outputs_contains!.toLowerCase())
    );
    if (!hasOutput) return false;
  }
  
  // Check video_outputs_any_of filter
  if (filter.video_outputs_any_of && filter.video_outputs_any_of.length > 0) {
    const hasAnyOutput = filter.video_outputs_any_of.some(filterOutput =>
      camera.videoOutputs.some(output => output.toLowerCase().includes(filterOutput.toLowerCase()))
    );
    if (!hasAnyOutput) return false;
  }
  
  // Check power_input_contains filter
  if (filter.power_input_contains) {
    const hasPower = camera.powerInput.some(power =>
      power.toLowerCase().includes(filter.power_input_contains!.toLowerCase())
    );
    if (!hasPower) return false;
  }
  
  // Check power_input_any_of filter
  if (filter.power_input_any_of && filter.power_input_any_of.length > 0) {
    const hasAnyPower = filter.power_input_any_of.some(filterPower =>
      camera.powerInput.some(power => power.toLowerCase().includes(filterPower.toLowerCase()))
    );
    if (!hasAnyPower) return false;
  }
  
  // Check lens_mount filter (for camera lens mount compatibility)
  if (filter.mount_type) {
    if (camera.lensMount.toLowerCase() !== filter.mount_type.toLowerCase()) return false;
  }
  
  return true;
};

export function useAccessoriesWithRules(options?: { demoOnly?: boolean }) {
  const [accessories, setAccessories] = useState<AccessorySpec[]>([]);
  const [rules, setRules] = useState<CompatibilityRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch accessories and rules from database
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        let accessoriesQuery = supabase.from('accessories').select('*').eq('is_active', true);
        
        // Filter by demo status if requested
        if (options?.demoOnly) {
          accessoriesQuery = (accessoriesQuery as any).eq('is_demo', true);
        }

        const [accessoriesRes, rulesRes] = await Promise.all([
          accessoriesQuery,
          supabase.from('compatibility_rules').select('*').eq('is_active', true),
        ]);

        if (accessoriesRes.data) {
          setAccessories(accessoriesRes.data.map(a => transformAccessory(a as unknown as DbAccessory)));
        }
        if (rulesRes.data) {
          setRules(rulesRes.data.map(r => ({
            ...r,
            target_filter: r.target_filter as TargetFilter | null,
            is_category_rule: r.is_category_rule ?? false,
            source_filter: r.source_filter as SourceFilter | null,
            is_global_rule: r.is_global_rule ?? false,
          })) as CompatibilityRule[]);
        }
      } catch (error) {
        console.error('Error fetching accessories and rules:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [options?.demoOnly]);

  // Build compatibility lookup maps
  const compatibilityMaps = useMemo(() => {
    // Camera -> Accessory compatibility (what accessories work with what cameras)
    const cameraToAccessory = new Map<string, Set<string>>();
    // Camera -> Accessory incompatibility (what accessories DON'T work with what cameras)
    const cameraToAccessoryIncompat = new Map<string, Set<string>>();
    // Accessory -> Accessory conflicts
    const accessoryConflicts = new Map<string, Set<string>>();
    // Accessory -> Required accessories (ID-to-ID rules)
    const accessoryRequires = new Map<string, Set<string>>();
    // Accessory -> Category-based requirements (future-proof!)
    const accessoryCategoryRequires = new Map<string, Array<{
      category: string;
      filter: TargetFilter | null;
      notes: string | null;
    }>>();
    // Camera attribute-based global rules
    const cameraGlobalRules: Array<{
      sourceFilter: SourceFilter;
      targetId: string;
      relationship: string;
    }> = [];

    for (const rule of rules) {
      // Handle global camera rules (attribute-based)
      if (rule.is_global_rule && rule.source_filter) {
        cameraGlobalRules.push({
          sourceFilter: rule.source_filter,
          targetId: rule.target_id,
          relationship: rule.relationship,
        });
        continue;
      }
      
      if (rule.rule_type === 'camera_accessory') {
        if (rule.source_type === 'camera' && rule.target_type === 'accessory') {
          if (rule.relationship === 'compatible') {
            if (!cameraToAccessory.has(rule.source_id)) {
              cameraToAccessory.set(rule.source_id, new Set());
            }
            cameraToAccessory.get(rule.source_id)!.add(rule.target_id);
          } else if (rule.relationship === 'incompatible') {
            if (!cameraToAccessoryIncompat.has(rule.source_id)) {
              cameraToAccessoryIncompat.set(rule.source_id, new Set());
            }
            cameraToAccessoryIncompat.get(rule.source_id)!.add(rule.target_id);
          }
        }
      } else if (rule.rule_type === 'conflict' || rule.rule_type === 'accessory_accessory') {
        if (rule.relationship === 'conflicts') {
          if (!accessoryConflicts.has(rule.source_id)) {
            accessoryConflicts.set(rule.source_id, new Set());
          }
          accessoryConflicts.get(rule.source_id)!.add(rule.target_id);
          if (!accessoryConflicts.has(rule.target_id)) {
            accessoryConflicts.set(rule.target_id, new Set());
          }
          accessoryConflicts.get(rule.target_id)!.add(rule.source_id);
        } else if (rule.relationship === 'requires') {
          if (rule.is_category_rule && rule.target_category) {
            if (rule.source_type === 'accessory_category') {
              const categoryKey = `category:${rule.source_id}`;
              if (!accessoryCategoryRequires.has(categoryKey)) {
                accessoryCategoryRequires.set(categoryKey, []);
              }
              accessoryCategoryRequires.get(categoryKey)!.push({
                category: rule.target_category,
                filter: rule.target_filter,
                notes: rule.notes,
              });
            } else {
              if (!accessoryCategoryRequires.has(rule.source_id)) {
                accessoryCategoryRequires.set(rule.source_id, []);
              }
              accessoryCategoryRequires.get(rule.source_id)!.push({
                category: rule.target_category,
                filter: rule.target_filter,
                notes: rule.notes,
              });
            }
          } else {
            if (!accessoryRequires.has(rule.source_id)) {
              accessoryRequires.set(rule.source_id, new Set());
            }
            accessoryRequires.get(rule.source_id)!.add(rule.target_id);
          }
        }
      }
    }

    return {
      cameraToAccessory,
      cameraToAccessoryIncompat,
      accessoryConflicts,
      accessoryRequires,
      accessoryCategoryRequires,
      cameraGlobalRules,
    };
  }, [rules]);

  // Internal battery types that power cameras directly (not via plates)
  const INTERNAL_BATTERY_SIZES = ['np-f', 'bp-u', 'bp-a', 'np-fz100', 'dmw-blk22'];

  // Check if an internal battery's power output matches a camera's power input
  const isInternalBatteryCompatible = useCallback((
    battery: AccessorySpec,
    cameraPowerInput: string[]
  ): boolean => {
    if (!battery.powerOutput || battery.powerOutput.length === 0) return false;
    
    // Check if any battery output matches any camera input
    return battery.powerOutput.some(output => 
      cameraPowerInput.some(input => {
        const outputLower = output.toLowerCase();
        const inputLower = input.toLowerCase();
        // Match if output is contained in input or vice versa
        // e.g., "BP-U" matches "BP-U Series", "NP-FZ100" matches "NP-FZ100"
        return inputLower.includes(outputLower) || outputLower.includes(inputLower);
      })
    );
  }, []);

  // Get compatible accessories for a camera - combines database rules with accessory's camera_compatibility
  // Optionally pass camera's powerInput for internal battery filtering
  const getCompatibleAccessories = useCallback((
    cameraId: string,
    cameraPowerInput?: string[]
  ): AccessorySpec[] => {
    const dbRuleCompatible = compatibilityMaps.cameraToAccessory.get(cameraId) || new Set<string>();
    const dbRuleIncompatible = compatibilityMaps.cameraToAccessoryIncompat.get(cameraId) || new Set<string>();

    return accessories.filter(acc => {
      // If explicitly marked incompatible by a rule, exclude
      if (dbRuleIncompatible.has(acc.id)) {
        return false;
      }

      // Internal battery filtering - check power compatibility
      if (acc.category === 'battery' && acc.batterySize && INTERNAL_BATTERY_SIZES.includes(acc.batterySize)) {
        // If we have camera power input info, filter based on compatibility
        if (cameraPowerInput && cameraPowerInput.length > 0) {
          if (!isInternalBatteryCompatible(acc, cameraPowerInput)) {
            return false;
          }
        }
      }

      // If explicitly marked compatible by a rule, include
      if (dbRuleCompatible.has(acc.id)) {
        return true;
      }

      // Fall back to accessory's built-in camera_compatibility array
      return acc.cameraCompatibility.includes(cameraId) || 
             acc.cameraCompatibility.includes('universal');
    });
  }, [accessories, compatibilityMaps, isInternalBatteryCompatible]);

  // Check if two accessories conflict
  const hasConflict = useCallback((accessoryId1: string, accessoryId2: string): boolean => {
    const conflicts = compatibilityMaps.accessoryConflicts.get(accessoryId1);
    return conflicts?.has(accessoryId2) || false;
  }, [compatibilityMaps]);

  // Get all conflicts for an accessory
  const getConflicts = useCallback((accessoryId: string): Set<string> => {
    return compatibilityMaps.accessoryConflicts.get(accessoryId) || new Set();
  }, [compatibilityMaps]);

  // Get required accessories for an accessory (ID-to-ID only - legacy)
  const getRequirements = useCallback((accessoryId: string): Set<string> => {
    return compatibilityMaps.accessoryRequires.get(accessoryId) || new Set();
  }, [compatibilityMaps]);

  // Get all accessories that satisfy requirements for a source accessory (FUTURE-PROOF!)
  // This combines ID-to-ID rules with category-based rules
  const getRequirementMatches = useCallback((accessoryId: string): AccessorySpec[] => {
    const matches: AccessorySpec[] = [];
    const matchedIds = new Set<string>();
    
    // 1. Add direct ID matches (legacy ID-to-ID rules)
    const directIds = compatibilityMaps.accessoryRequires.get(accessoryId) || new Set();
    directIds.forEach(id => {
      const acc = accessories.find(a => a.id === id);
      if (acc && !matchedIds.has(acc.id)) {
        matches.push(acc);
        matchedIds.add(acc.id);
      }
    });
    
    // 2. Add category-based matches (FUTURE-PROOF!)
    const categoryRules = compatibilityMaps.accessoryCategoryRequires.get(accessoryId) || [];
    categoryRules.forEach(rule => {
      accessories
        .filter(a => a.category === rule.category)
        .filter(a => matchesFilter(a, rule.filter))
        .forEach(a => {
          if (!matchedIds.has(a.id)) {
            matches.push(a);
            matchedIds.add(a.id);
          }
        });
    });
    
    return matches;
  }, [accessories, compatibilityMaps]);

  // Get category-based requirements for an accessory (for display purposes)
  // This combines individual accessory rules with category-level rules
  const getCategoryRequirements = useCallback((accessoryId: string, accessoryCategory?: string): Array<{
    category: string;
    filter: TargetFilter | null;
    notes: string | null;
  }> => {
    const results: Array<{ category: string; filter: TargetFilter | null; notes: string | null }> = [];
    
    // Get individual accessory rules
    const individualRules = compatibilityMaps.accessoryCategoryRequires.get(accessoryId) || [];
    results.push(...individualRules);
    
    // Get category-level rules (e.g., all batteries require battery-plate)
    if (accessoryCategory) {
      const categoryKey = `category:${accessoryCategory}`;
      const categoryRules = compatibilityMaps.accessoryCategoryRequires.get(categoryKey) || [];
      results.push(...categoryRules);
    }
    
    return results;
  }, [compatibilityMaps]);

  // Check if a requirement is satisfied by selected accessories
  const isRequirementSatisfied = useCallback((
    accessoryId: string,
    selectedAccessoryIds: string[],
    accessoryCategory?: string
  ): { satisfied: boolean; missingCategories: string[] } => {
    const selectedSet = new Set(selectedAccessoryIds);
    const missingCategories: string[] = [];
    
    // Check ID-to-ID requirements
    const directRequirements = compatibilityMaps.accessoryRequires.get(accessoryId) || new Set();
    const hasDirectMatch = directRequirements.size === 0 || 
      Array.from(directRequirements).some(id => selectedSet.has(id));
    
    // Collect all category rules (individual + category-level)
    const individualRules = compatibilityMaps.accessoryCategoryRequires.get(accessoryId) || [];
    const categoryLevelRules = accessoryCategory 
      ? compatibilityMaps.accessoryCategoryRequires.get(`category:${accessoryCategory}`) || []
      : [];
    const allCategoryRules = [...individualRules, ...categoryLevelRules];
    
    for (const rule of allCategoryRules) {
      const matchingAccessories = accessories
        .filter(a => a.category === rule.category)
        .filter(a => matchesFilter(a, rule.filter));
      
      const hasCategoryMatch = matchingAccessories.some(a => {
        // Check direct ID match
        if (selectedSet.has(a.id)) return true;
        // Check if any selected ID is an instance ID matching this accessory's original ID
        return selectedAccessoryIds.some(selectedId => {
          if (selectedId.includes('::')) {
            const originalId = selectedId.split('::')[0];
            return originalId === a.id;
          }
          return false;
        });
      });
      if (!hasCategoryMatch && matchingAccessories.length > 0) {
        let filterDesc = '';
        if (rule.filter?.name_contains) {
          filterDesc = rule.filter.name_contains;
        } else if (rule.filter?.all_of && rule.filter.all_of.length > 0) {
          filterDesc = rule.filter.all_of.join(' + ');
        } else if (rule.filter?.any_of && rule.filter.any_of.length > 0) {
          filterDesc = rule.filter.any_of.join('/');
        }
        // Format as "D-Tap cable" instead of "cable (D-Tap)"
        const label = filterDesc ? `${filterDesc} ${rule.category}` : rule.category;
        missingCategories.push(label);
      }
    }
    
    const satisfied = hasDirectMatch && missingCategories.length === 0;
    return { satisfied, missingCategories };
  }, [accessories, compatibilityMaps]);

  // Check if an accessory has any rule-based incompatibility with selected accessories
  const hasRuleBasedIncompatibility = useCallback((
    accessoryId: string, 
    selectedAccessoryIds: string[]
  ): { hasConflict: boolean; conflictWith: string[] } => {
    const conflicts = getConflicts(accessoryId);
    const conflictWith = selectedAccessoryIds.filter(id => conflicts.has(id));
    return {
      hasConflict: conflictWith.length > 0,
      conflictWith,
    };
  }, [getConflicts]);

  // Check if an accessory is compatible with a camera based on global rules
  const checkCameraGlobalRules = useCallback((
    accessoryId: string,
    camera: Camera
  ): { isCompatible: boolean; isIncompatible: boolean } => {
    let isCompatible = false;
    let isIncompatible = false;
    
    for (const rule of compatibilityMaps.cameraGlobalRules) {
      if (rule.targetId === accessoryId && matchesCameraFilter(camera, rule.sourceFilter)) {
        if (rule.relationship === 'compatible') isCompatible = true;
        if (rule.relationship === 'incompatible') isIncompatible = true;
      }
    }
    
    return { isCompatible, isIncompatible };
  }, [compatibilityMaps]);

  return {
    accessories,
    rules,
    isLoading,
    getCompatibleAccessories,
    hasConflict,
    getConflicts,
    getRequirements,
    getRequirementMatches,
    getCategoryRequirements,
    isRequirementSatisfied,
    hasRuleBasedIncompatibility,
    checkCameraGlobalRules,
    compatibilityMaps,
  };
}
